import { Node as ProseMirrorNode } from "prosemirror-model";
import {
  initProseMirrorDoc,
  prosemirrorToYDoc,
  updateYFragment,
  yXmlFragmentToProseMirrorRootNode,
} from "y-prosemirror";
import * as Y from "yjs";

import { schema } from "../schema/index.js";

export * from "./version.js";

/** The shared Yjs XML fragment the collaborative editor binds to. */
export const COLLAB_FRAGMENT_FIELD = "default";

/** Builds the collaborative state for a document that has none yet. */
export function docToYDoc(doc: ProseMirrorNode): Y.Doc {
  return prosemirrorToYDoc(doc, COLLAB_FRAGMENT_FIELD);
}

/** Projects the collaborative state back into a schema-valid document. */
export function yDocToDoc(ydoc: Y.Doc): ProseMirrorNode {
  return yXmlFragmentToProseMirrorRootNode(ydoc.getXmlFragment(COLLAB_FRAGMENT_FIELD), schema);
}

/** The binary `state` column: a full Yjs state vector update. */
export function encodeState(ydoc: Y.Doc): Uint8Array {
  return Y.encodeStateAsUpdate(ydoc);
}

export function decodeState(state: Uint8Array): Y.Doc {
  const ydoc = new Y.Doc();
  Y.applyUpdate(ydoc, state);
  return ydoc;
}

/**
 * Folds a ProseMirror document into an existing replica as a **structural diff**: unchanged
 * nodes keep their CRDT identity, so positions anchored inside them — remote carets, comment
 * marks, selections — survive, where clearing the fragment and re-inserting would read as
 * "everyone else's text was deleted and mine was added".
 *
 * The replica ends up equal to `doc`, so anything it holds that `doc` does not is deleted —
 * including text a collaborator typed after `doc` was read. A live session therefore takes an
 * API write through `mergeStoredState`, which adds the write and removes nothing; this fold is
 * only its fallback for a stored replica that shares no history with the session.
 *
 * The whole diff runs in one transaction tagged with `origin`, which is how a subscriber can
 * tell its own writes apart from a client's and avoid feeding them back.
 */
export function mergeDocIntoYDoc(ydoc: Y.Doc, doc: ProseMirrorNode, origin?: unknown): void {
  const fragment = ydoc.getXmlFragment(COLLAB_FRAGMENT_FIELD);
  ydoc.transact(() => {
    updateYFragment(ydoc, fragment, doc, { mapping: new Map(), isOMark: new Map() });
  }, origin);
}

/**
 * Mutates an existing replica so that it holds `doc`, by structural diff against what it
 * already holds. This is the only correct way for the API to write into a document that
 * live sessions are editing: building a fresh replica instead would give every node new
 * internal identities and merge as duplicated content against any client still holding the
 * original (`docs/spec/comments.md` §6, step 4).
 */
export function applyDocToYDoc(ydoc: Y.Doc, doc: ProseMirrorNode): void {
  const fragment = ydoc.getXmlFragment(COLLAB_FRAGMENT_FIELD);
  // `meta` carries the node↔element mapping the diff walks; it is built by reading the
  // fragment back, which is exactly the state the diff has to be taken against.
  const { meta } = initProseMirrorDoc(fragment, schema);
  ydoc.transact(() => {
    updateYFragment(ydoc, fragment, doc, meta);
  });
}

/** What `mergeStoredState` did with a stored replica. */
export type StoredStateMerge = "merged" | "unrelated";

/**
 * Brings a live replica up to date with a stored one by applying exactly the operations the
 * live replica is missing (`docs/spec/collaboration.md` §14, subscribe side). Whatever the live
 * replica already holds — including edits made after the stored one was written — is left
 * alone, and merging the same stored state again changes nothing.
 *
 * That holds only for replicas that share a history: a stored state written from the session,
 * or folded forward from one the session hydrated from. Two replicas built separately give the
 * same text different identities, and merging them repeats every node. A stored state with no
 * client in common with the live replica is therefore refused as `"unrelated"`, and the live
 * replica is not touched.
 */
export function mergeStoredState(
  live: Y.Doc,
  stored: Uint8Array,
  origin?: unknown,
): StoredStateMerge {
  const liveVector = Y.encodeStateVector(live);
  const liveClients = Y.decodeStateVector(liveVector);
  const storedClients = Y.decodeStateVector(Y.encodeStateVectorFromUpdate(stored));
  const unrelated =
    liveClients.size > 0 &&
    storedClients.size > 0 &&
    ![...storedClients.keys()].some((client) => liveClients.has(client));
  if (unrelated) {
    return "unrelated";
  }
  Y.applyUpdate(live, Y.diffUpdate(stored, liveVector), origin);
  return "merged";
}

/**
 * The replica a document should be edited through: its stored state when it has one, and
 * otherwise one built from `content` exactly as hydration would. Building it even when
 * there is no state yet is what makes the write publish something a live editor can apply
 * (`docs/spec/comments.md` edge case 3).
 */
export function replicaFor(state: Uint8Array | null | undefined, doc: ProseMirrorNode): Y.Doc {
  return state === null || state === undefined || state.length === 0
    ? docToYDoc(doc)
    : decodeState(state);
}
